// Module Configuration Système - Interface Admin
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  FiSettings, FiSliders, FiZap, FiBell, FiMail, FiGlobe,
  FiSave, FiRotateCcw, FiCpu, FiHome, FiTrendingUp, FiPlus, FiTrash2, FiCheck
} from 'react-icons/fi';
import { ScoringConfigurationServices } from '@/services/ScoringConfigurationServices';
import { ScoringConfiguration } from '@/models';
import { useNotification } from '@/contexts/NotificationContext';

// Types
interface ScoringWeight {
  id: string;
  label: string;
  weight: number;
  description: string;
}

interface HousingThreshold {
  type: string;
  minScore: number;
  maxScore: number;
  priority: number;
}

interface WorkflowStep {
  id: string;
  name: string;
  duration: number; // in hours
  autoEscalate: boolean;
  notifyOnDelay: boolean;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  ${props => props.variant === 'secondary' ? `
    background: transparent;
    color: ${theme.colors.text};
    border: 1px solid ${theme.colors.surface};
    &:hover {
      background: ${theme.colors.surface};
    }
  ` : `
    background: ${theme.colors.primary};
    color: white;
    &:hover {
      background: ${theme.colors.primary}DD;
      transform: translateY(-2px);
    }
  `}
`;

const TabBar = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const Tab = styled.button<{ active?: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${props => props.active ? theme.colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : theme.colors.text};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => props.active ? theme.colors.primary + 'DD' : theme.colors.surface};
  }
`;

const Section = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const SectionDescription = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0 0 ${theme.spacing.xl} 0;
`;

const WeightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const WeightItem = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
`;

const WeightHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${theme.spacing.md};
`;

const WeightInfo = styled.div`
  flex: 1;
`;

const WeightLabel = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const WeightDescription = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const WeightControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  min-width: 200px;
`;

const Slider = styled.input`
  flex: 1;
  height: 6px;
  border-radius: ${theme.borderRadius.full};
  outline: none;
  -webkit-appearance: none;
  background: ${theme.colors.primary};/*
    ${theme.colors.primary} 0%,
    ${theme.colors.primary} var(--value),
    ${theme.colors.surface} var(--value),
    ${theme.colors.surface} 100%);

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${theme.colors.primary};
    cursor: pointer;
    box-shadow: ${theme.shadows.sm};
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${theme.colors.primary};
    cursor: pointer;
    border: none;
    box-shadow: ${theme.shadows.sm};
  }
`;

const WeightValue = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  min-width: 50px;
  text-align: right;
`;

const ThresholdGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
`;

const ThresholdCard = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.primary};
`;

const ThresholdType = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.md};

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text}80;
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const WorkflowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const WorkflowStep = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StepInfo = styled.div`
  flex: 1;
`;

const StepName = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const StepMeta = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const StepControls = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  align-items: center;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  input:checked + span {
    background-color: ${theme.colors.success};
  }

  input:checked + span:before {
    transform: translateX(24px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${theme.colors.text}40;
  transition: 0.3s;
  border-radius: ${theme.borderRadius.full};

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const CustomizationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.xl};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ColorPicker = styled.input`
  width: 60px;
  height: 40px;
  border: 2px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: ${theme.borderRadius.sm};
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ConfigSelector = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const ConfigList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
`;

const ConfigCard = styled.div<{ active?: boolean; selected?: boolean }>`
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${props =>
    props.active ? theme.colors.success :
    props.selected ? theme.colors.primary :
    theme.colors.surface
  };
  background: ${props =>
    props.active ? theme.colors.success + '10' :
    props.selected ? theme.colors.primary + '10' :
    'white'
  };
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
`;

const ConfigCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${theme.spacing.sm};
`;

const ConfigName = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const ConfigBadge = styled.span<{ type: 'active' | 'inactive' }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  background: ${props => props.type === 'active' ? theme.colors.success : theme.colors.text}20;
  color: ${props => props.type === 'active' ? theme.colors.success : theme.colors.text}80;
`;

const ConfigMeta = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-bottom: ${theme.spacing.md};
`;

const ConfigActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
`;

const SmallButton = styled.button<{ variant?: 'primary' | 'danger' | 'success' }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: all ${theme.transitions.fast};

  ${props => {
    if (props.variant === 'danger') {
      return `
        background: ${theme.colors.error};
        color: white;
        &:hover { background: ${theme.colors.error}DD; }
      `;
    } else if (props.variant === 'success') {
      return `
        background: ${theme.colors.success};
        color: white;
        &:hover { background: ${theme.colors.success}DD; }
      `;
    } else {
      return `
        background: ${theme.colors.primary};
        color: white;
        &:hover { background: ${theme.colors.primary}DD; }
      `;
    }
  }}
`;

export const SystemConfiguration: React.FC = () => {
  const notification = useNotification();
  const [activeTab, setActiveTab] = useState<'scoring' | 'workflow' | 'customization'>('scoring');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Liste de toutes les configurations
  const [allConfigs, setAllConfigs] = useState<ScoringConfiguration[]>([]);

  // Configuration actuellement éditée
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  // Formulaire
  const [configName, setConfigName] = useState('');
  const [configDescription, setConfigDescription] = useState('');
  const [configVersion, setConfigVersion] = useState('1.0.0');

  // Seuils financiers
  const [seuilRevenusMinimal, setSeuilRevenusMinimal] = useState(2500000);
  const [seuilRavRecommande, setSeuilRavRecommande] = useState(500000);
  const [plafondRevenusAnnuel, setPlafondRevenusAnnuel] = useState(35362140);

  // Points financiers
  const [pointsFinancialRevenus, setPointsFinancialRevenus] = useState(40);
  const [pointsFinancialEndettement, setPointsFinancialEndettement] = useState(30);
  const [pointsFinancialRav, setPointsFinancialRav] = useState(20);
  const [pointsFinancialEpargne, setPointsFinancialEpargne] = useState(10);

  // Coefficients financiers
  const [coefTauxEndettement, setCoefTauxEndettement] = useState(0.75);
  const [tauxEndettementDefaut, setTauxEndettementDefaut] = useState(0.33);
  const [moisLoyerEpargne, setMoisLoyerEpargne] = useState(3);
  const [seuilEpargneHeuristique, setSeuilEpargneHeuristique] = useState(1000000);
  const [multiEpargneHeuristique, setMultiEpargneHeuristique] = useState(5);

  // Scores Employés
  const [scoreCdiBase, setScoreCdiBase] = useState(90);
  const [scoreCdiMultiplier, setScoreCdiMultiplier] = useState(2.0);
  const [scoreCddBase, setScoreCddBase] = useState(75);
  const [scoreCddMultiplier, setScoreCddMultiplier] = useState(1.5);
  const [scoreFonctionnaireBase, setScoreFonctionnaireBase] = useState(95);
  const [scoreFonctionnaireMultiplier, setScoreFonctionnaireMultiplier] = useState(2.5);
  const [scoreStageBase, setScoreStageBase] = useState(50);
  const [scoreStageMultiplier, setScoreStageMultiplier] = useState(1.0);
  const [scoreEssaiBase, setScoreEssaiBase] = useState(70);
  const [scoreEssaiMultiplier, setScoreEssaiMultiplier] = useState(1.0);

  // Scores Entrepreneurs par ancienneté
  const [scoreEntrepreneur36Plus, setScoreEntrepreneur36Plus] = useState(85);
  const [scoreEntrepreneur24_36, setScoreEntrepreneur24_36] = useState(75);
  const [scoreEntrepreneur12_24, setScoreEntrepreneur12_24] = useState(60);
  const [scoreEntrepreneurMoins12, setScoreEntrepreneurMoins12] = useState(40);
  const [multiplierEntrepreneur36Plus, setMultiplierEntrepreneur36Plus] = useState(2.0);
  const [multiplierEntrepreneur24_36, setMultiplierEntrepreneur24_36] = useState(1.8);
  const [multiplierEntrepreneur12_24, setMultiplierEntrepreneur12_24] = useState(1.5);
  const [multiplierEntrepreneurMoins12, setMultiplierEntrepreneurMoins12] = useState(1.0);

  // Scores Secteur informel par ancienneté
  const [scoreInformel36Plus, setScoreInformel36Plus] = useState(75);
  const [scoreInformel24_36, setScoreInformel24_36] = useState(65);
  const [scoreInformel12_24, setScoreInformel12_24] = useState(55);
  const [scoreInformelMoins12, setScoreInformelMoins12] = useState(35);
  const [multiplierInformel36Plus, setMultiplierInformel36Plus] = useState(1.8);
  const [multiplierInformel24_36, setMultiplierInformel24_36] = useState(1.6);
  const [multiplierInformel12_24, setMultiplierInformel12_24] = useState(1.3);
  const [multiplierInformelMoins12, setMultiplierInformelMoins12] = useState(1.0);

  // Seuils CA pour entrepreneurs
  const [seuilCaEleve, setSeuilCaEleve] = useState(5000000);
  const [seuilCaMoyen, setSeuilCaMoyen] = useState(2000000);
  const [seuilCaBas, setSeuilCaBas] = useState(1000000);
  const [bonusCaEleve, setBonusCaEleve] = useState(10);
  const [bonusCaMoyen, setBonusCaMoyen] = useState(5);
  const [bonusCaBas, setBonusCaBas] = useState(3);

  // Score minimum d'éligibilité
  const [scoreMinimumEligibilite, setScoreMinimumEligibilite] = useState(40);

  // Bonifications et malus professionnels
  const [bonusProfPatente, setBonusProfPatente] = useState(5);
  const [bonusProfEmployes, setBonusProfEmployes] = useState(3);
  const [bonusProfContrats, setBonusProfContrats] = useState(5);
  const [bonusProfEvolutionHausse, setBonusProfEvolutionHausse] = useState(5);
  const [malusProfEvolutionBaisse, setMalusProfEvolutionBaisse] = useState(5);

  // Bonifications secteur informel
  const [bonusInformelAssociation, setBonusInformelAssociation] = useState(5);
  const [bonusInformelClients10Plus, setBonusInformelClients10Plus] = useState(5);
  const [bonusInformelClients5Plus, setBonusInformelClients5Plus] = useState(3);
  const [bonusInformelMarche, setBonusInformelMarche] = useState(3);
  const [bonusInformelLocalFixe, setBonusInformelLocalFixe] = useState(3);
  const [bonusInformelEpargne, setBonusInformelEpargne] = useState(3);
  const [bonusInformelMobileMoney, setBonusInformelMobileMoney] = useState(2);

  // Bonifications et malus documentaires
  const [bonusDocTousVerifies, setBonusDocTousVerifies] = useState(5);
  const [bonusDocTousLisibles, setBonusDocTousLisibles] = useState(5);
  const [bonusDocDossierComplet, setBonusDocDossierComplet] = useState(3);
  const [malusDocEssentielManquant, setMalusDocEssentielManquant] = useState(5);
  const [malusDocPeuDocuments, setMalusDocPeuDocuments] = useState(10);

  // Points personnels - Ancienneté demande
  const [pointsPersonalAnc36Plus, setPointsPersonalAnc36Plus] = useState(20);
  const [pointsPersonalAnc24_36, setPointsPersonalAnc24_36] = useState(15);
  const [pointsPersonalAnc12_24, setPointsPersonalAnc12_24] = useState(10);
  const [pointsPersonalAnc6_12, setPointsPersonalAnc6_12] = useState(5);

  // Points personnels - Famille
  const [pointsPersonalFamille3Plus, setPointsPersonalFamille3Plus] = useState(15);
  const [pointsPersonalFamille2Plus, setPointsPersonalFamille2Plus] = useState(10);
  const [pointsPersonalParentIsole, setPointsPersonalParentIsole] = useState(10);

  // Points personnels - Logement
  const [pointsPersonalLogementRue, setPointsPersonalLogementRue] = useState(25);
  const [pointsPersonalLogementInsalubre, setPointsPersonalLogementInsalubre] = useState(20);
  const [pointsPersonalLogementHeberge, setPointsPersonalLogementHeberge] = useState(15);

  // Points personnels - Profession prioritaire
  const [pointsPersonalProfPrioritaire, setPointsPersonalProfPrioritaire] = useState(10);

  const [scoringWeights, setScoringWeights] = useState<ScoringWeight[]>([
    {
      id: 'financial',
      label: 'Critère Financier',
      weight: 35,
      description: 'Revenus, taux d\'endettement, reste à vivre, épargne'
    },
    {
      id: 'professional',
      label: 'Critère Professionnel',
      weight: 25,
      description: 'Type de contrat, ancienneté, stabilité professionnelle'
    },
    {
      id: 'documentary',
      label: 'Critère Documentaire',
      weight: 20,
      description: 'Complétude et qualité des documents fournis'
    },
    {
      id: 'eligibility',
      label: 'Critère d\'Éligibilité',
      weight: 15,
      description: 'Conformité aux critères d\'éligibilité de base'
    },
    {
      id: 'personal',
      label: 'Critère Personnel',
      weight: 5,
      description: 'Ancienneté commune, situation familiale, handicap'
    }
  ]);

  const [thresholds, setThresholds] = useState<HousingThreshold[]>([
    { type: 'T1', minScore: 60, maxScore: 100, priority: 1 },
    { type: 'T2', minScore: 65, maxScore: 100, priority: 2 },
    { type: 'T3', minScore: 70, maxScore: 100, priority: 3 },
    { type: 'T4', minScore: 75, maxScore: 100, priority: 4 },
    { type: 'T5+', minScore: 80, maxScore: 100, priority: 5 }
  ]);

  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([
    {
      id: '1',
      name: 'Vérification Documents',
      duration: 24,
      autoEscalate: true,
      notifyOnDelay: true
    },
    {
      id: '2',
      name: 'Pré-sélection (IA)',
      duration: 2,
      autoEscalate: false,
      notifyOnDelay: false
    },
    {
      id: '3',
      name: 'Validation Manager',
      duration: 48,
      autoEscalate: true,
      notifyOnDelay: true
    },
    {
      id: '4',
      name: 'Scoring Final',
      duration: 1,
      autoEscalate: false,
      notifyOnDelay: false
    },
    {
      id: '5',
      name: 'Attribution Commission',
      duration: 168,
      autoEscalate: false,
      notifyOnDelay: true
    }
  ]);

  // Charger toutes les configurations
  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const response = await ScoringConfigurationServices.list() as any;

      if (response.success && response.data) {
        const configs = response.data.results || response.data;
        setAllConfigs(configs);

        // Sélectionner automatiquement la configuration active
        const activeConfig = configs.find((c: ScoringConfiguration) => c.is_active);
        if (activeConfig && !selectedConfigId) {
          setSelectedConfigId(activeConfig.id);
          loadConfigIntoForm(activeConfig);
        }
      }
    } catch (error: any) {
      console.error('Error loading configurations:', error);
      notification.toast.error('Erreur lors du chargement des configurations');
    } finally {
      setLoading(false);
    }
  };

  // Charger une configuration dans le formulaire
  const loadConfigIntoForm = (config: ScoringConfiguration) => {
    setConfigName(config.name);
    setConfigDescription(config.description || '');
    setConfigVersion(config.version);

    // Charger les poids
    setScoringWeights([
      {
        id: 'financial',
        label: 'Critère Financier',
        weight: Math.round(config.weight_financial * 100),
        description: 'Revenus, taux d\'endettement, reste à vivre, épargne'
      },
      {
        id: 'professional',
        label: 'Critère Professionnel',
        weight: Math.round(config.weight_professional * 100),
        description: 'Type de contrat, ancienneté, stabilité professionnelle'
      },
      {
        id: 'documentary',
        label: 'Critère Documentaire',
        weight: Math.round(config.weight_documentary * 100),
        description: 'Complétude et qualité des documents fournis'
      },
      {
        id: 'eligibility',
        label: 'Critère d\'Éligibilité',
        weight: Math.round(config.weight_eligibility * 100),
        description: 'Conformité aux critères d\'éligibilité de base'
      },
      {
        id: 'personal',
        label: 'Critère Personnel',
        weight: Math.round(config.weight_personal * 100),
        description: 'Ancienneté commune, situation familiale, handicap'
      }
    ]);

    // Charger les seuils financiers
    setSeuilRevenusMinimal(Number(config.seuil_revenus_minimal));
    setSeuilRavRecommande(Number(config.seuil_rav_recommande));
    setPlafondRevenusAnnuel(Number(config.plafond_revenus_annuel));

    // Charger les points financiers
    setPointsFinancialRevenus(config.points_financial_revenus_stables);
    setPointsFinancialEndettement(config.points_financial_taux_endettement);
    setPointsFinancialRav(config.points_financial_reste_vivre);
    setPointsFinancialEpargne(config.points_financial_epargne);

    // Charger les coefficients financiers
    if (config.coef_taux_endettement !== undefined) setCoefTauxEndettement(Number(config.coef_taux_endettement));
    if (config.taux_endettement_defaut !== undefined) setTauxEndettementDefaut(Number(config.taux_endettement_defaut));
    if (config.mois_loyer_epargne !== undefined) setMoisLoyerEpargne(Number(config.mois_loyer_epargne));
    if (config.seuil_epargne_heuristique !== undefined) setSeuilEpargneHeuristique(Number(config.seuil_epargne_heuristique));
    if (config.multi_epargne_heuristique !== undefined) setMultiEpargneHeuristique(Number(config.multi_epargne_heuristique));

    // Charger les scores employés
    if (config.score_cdi_base !== undefined) setScoreCdiBase(Number(config.score_cdi_base));
    if (config.score_cdi_multiplier !== undefined) setScoreCdiMultiplier(Number(config.score_cdi_multiplier));
    if (config.score_cdd_base !== undefined) setScoreCddBase(Number(config.score_cdd_base));
    if (config.score_cdd_multiplier !== undefined) setScoreCddMultiplier(Number(config.score_cdd_multiplier));
    if (config.score_fonctionnaire_base !== undefined) setScoreFonctionnaireBase(Number(config.score_fonctionnaire_base));
    if (config.score_fonctionnaire_multiplier !== undefined) setScoreFonctionnaireMultiplier(Number(config.score_fonctionnaire_multiplier));
    if (config.score_stage_base !== undefined) setScoreStageBase(Number(config.score_stage_base));
    if (config.score_stage_multiplier !== undefined) setScoreStageMultiplier(Number(config.score_stage_multiplier));
    if (config.score_essai_base !== undefined) setScoreEssaiBase(Number(config.score_essai_base));
    if (config.score_essai_multiplier !== undefined) setScoreEssaiMultiplier(Number(config.score_essai_multiplier));

    // Charger les scores entrepreneurs
    if (config.score_entrepreneur_36plus !== undefined) setScoreEntrepreneur36Plus(Number(config.score_entrepreneur_36plus));
    if (config.score_entrepreneur_24_36 !== undefined) setScoreEntrepreneur24_36(Number(config.score_entrepreneur_24_36));
    if (config.score_entrepreneur_12_24 !== undefined) setScoreEntrepreneur12_24(Number(config.score_entrepreneur_12_24));
    if (config.score_entrepreneur_moins_12 !== undefined) setScoreEntrepreneurMoins12(Number(config.score_entrepreneur_moins_12));
    if (config.multiplier_entrepreneur_36plus !== undefined) setMultiplierEntrepreneur36Plus(Number(config.multiplier_entrepreneur_36plus));
    if (config.multiplier_entrepreneur_24_36 !== undefined) setMultiplierEntrepreneur24_36(Number(config.multiplier_entrepreneur_24_36));
    if (config.multiplier_entrepreneur_12_24 !== undefined) setMultiplierEntrepreneur12_24(Number(config.multiplier_entrepreneur_12_24));
    if (config.multiplier_entrepreneur_moins_12 !== undefined) setMultiplierEntrepreneurMoins12(Number(config.multiplier_entrepreneur_moins_12));

    // Charger les scores secteur informel
    if (config.score_informel_36plus !== undefined) setScoreInformel36Plus(Number(config.score_informel_36plus));
    if (config.score_informel_24_36 !== undefined) setScoreInformel24_36(Number(config.score_informel_24_36));
    if (config.score_informel_12_24 !== undefined) setScoreInformel12_24(Number(config.score_informel_12_24));
    if (config.score_informel_moins_12 !== undefined) setScoreInformelMoins12(Number(config.score_informel_moins_12));
    if (config.multiplier_informel_36plus !== undefined) setMultiplierInformel36Plus(Number(config.multiplier_informel_36plus));
    if (config.multiplier_informel_24_36 !== undefined) setMultiplierInformel24_36(Number(config.multiplier_informel_24_36));
    if (config.multiplier_informel_12_24 !== undefined) setMultiplierInformel12_24(Number(config.multiplier_informel_12_24));
    if (config.multiplier_informel_moins_12 !== undefined) setMultiplierInformelMoins12(Number(config.multiplier_informel_moins_12));

    // Charger les seuils CA entrepreneurs
    if (config.seuil_ca_eleve !== undefined) setSeuilCaEleve(Number(config.seuil_ca_eleve));
    if (config.seuil_ca_moyen !== undefined) setSeuilCaMoyen(Number(config.seuil_ca_moyen));
    if (config.seuil_ca_bas !== undefined) setSeuilCaBas(Number(config.seuil_ca_bas));
    if (config.bonus_ca_eleve !== undefined) setBonusCaEleve(Number(config.bonus_ca_eleve));
    if (config.bonus_ca_moyen !== undefined) setBonusCaMoyen(Number(config.bonus_ca_moyen));
    if (config.bonus_ca_bas !== undefined) setBonusCaBas(Number(config.bonus_ca_bas));

    // Charger le score minimum
    setScoreMinimumEligibilite(Number(config.score_minimum_eligibilite));

    // Charger bonifications/malus professionnels
    setBonusProfPatente(config.bonus_professionnel_patente);
    setBonusProfEmployes(config.bonus_professionnel_employes);
    setBonusProfContrats(config.bonus_professionnel_contrats);
    setBonusProfEvolutionHausse(config.bonus_professionnel_evolution_hausse);
    setMalusProfEvolutionBaisse(config.malus_professionnel_evolution_baisse);

    // Charger bonifications secteur informel
    setBonusInformelAssociation(config.bonus_informel_association);
    setBonusInformelClients10Plus(config.bonus_informel_clients_10plus);
    setBonusInformelClients5Plus(config.bonus_informel_clients_5plus);
    setBonusInformelMarche(config.bonus_informel_marche);
    setBonusInformelLocalFixe(config.bonus_informel_local_fixe);
    setBonusInformelEpargne(config.bonus_informel_epargne);
    setBonusInformelMobileMoney(config.bonus_informel_mobile_money);

    // Charger bonifications/malus documentaires
    setBonusDocTousVerifies(config.bonus_documentary_tous_verifies);
    setBonusDocTousLisibles(config.bonus_documentary_tous_lisibles);
    setBonusDocDossierComplet(config.bonus_documentary_dossier_complet);
    setMalusDocEssentielManquant(config.malus_documentary_document_essentiel);
    setMalusDocPeuDocuments(config.malus_documentary_peu_documents);

    // Charger points personnels - Ancienneté
    setPointsPersonalAnc36Plus(config.points_personal_anciennete_36plus);
    setPointsPersonalAnc24_36(config.points_personal_anciennete_24_36);
    setPointsPersonalAnc12_24(config.points_personal_anciennete_12_24);
    setPointsPersonalAnc6_12(config.points_personal_anciennete_6_12);

    // Charger points personnels - Famille
    setPointsPersonalFamille3Plus(config.points_personal_famille_3plus);
    setPointsPersonalFamille2Plus(config.points_personal_famille_2plus);
    setPointsPersonalParentIsole(config.points_personal_parent_isole);

    // Charger points personnels - Logement
    setPointsPersonalLogementRue(config.points_personal_logement_rue);
    setPointsPersonalLogementInsalubre(config.points_personal_logement_insalubre);
    setPointsPersonalLogementHeberge(config.points_personal_logement_heberge);

    // Charger points personnels - Profession prioritaire
    setPointsPersonalProfPrioritaire(config.points_personal_profession_prioritaire);
  };

  // Charger au montage
  useEffect(() => {
    loadConfigurations();
  }, []);

  const handleWeightChange = (id: string, value: number) => {
    setScoringWeights(weights =>
      weights.map(w => w.id === id ? { ...w, weight: value } : w)
    );
  };

  // Sélectionner une configuration existante
  const handleSelectConfig = (configId: number) => {
    const config = allConfigs.find(c => c.id === configId);
    if (config) {
      setSelectedConfigId(configId);
      setIsCreatingNew(false);
      loadConfigIntoForm(config);
    }
  };

  // Créer une nouvelle configuration
  const handleCreateNew = () => {
    setIsCreatingNew(true);
    setSelectedConfigId(null);
    setConfigName('Nouvelle Configuration');
    setConfigDescription('');
    setConfigVersion('1.0.0');

    // Reset aux valeurs par défaut - Poids
    setScoringWeights([
      { id: 'financial', label: 'Critère Financier', weight: 35, description: 'Revenus, taux d\'endettement, reste à vivre, épargne' },
      { id: 'professional', label: 'Critère Professionnel', weight: 25, description: 'Type de contrat, ancienneté, stabilité professionnelle' },
      { id: 'documentary', label: 'Critère Documentaire', weight: 20, description: 'Complétude et qualité des documents fournis' },
      { id: 'eligibility', label: 'Critère d\'Éligibilité', weight: 15, description: 'Conformité aux critères d\'éligibilité de base' },
      { id: 'personal', label: 'Critère Personnel', weight: 5, description: 'Ancienneté commune, situation familiale, handicap' }
    ]);

    // Reset aux valeurs par défaut - Seuils
    setSeuilRevenusMinimal(2500000);
    setSeuilRavRecommande(500000);
    setPlafondRevenusAnnuel(35362140);

    // Reset aux valeurs par défaut - Points
    setPointsFinancialRevenus(40);
    setPointsFinancialEndettement(30);
    setPointsFinancialRav(20);
    setPointsFinancialEpargne(10);

    // Reset coefficients financiers
    setCoefTauxEndettement(0.75);
    setTauxEndettementDefaut(0.33);
    setMoisLoyerEpargne(3);
    setSeuilEpargneHeuristique(1000000);
    setMultiEpargneHeuristique(5);

    // Reset scores employés
    setScoreCdiBase(90);
    setScoreCdiMultiplier(2.0);
    setScoreCddBase(75);
    setScoreCddMultiplier(1.5);
    setScoreFonctionnaireBase(95);
    setScoreFonctionnaireMultiplier(2.5);
    setScoreStageBase(50);
    setScoreStageMultiplier(1.0);
    setScoreEssaiBase(70);
    setScoreEssaiMultiplier(1.0);

    // Reset scores entrepreneurs
    setScoreEntrepreneur36Plus(85);
    setScoreEntrepreneur24_36(75);
    setScoreEntrepreneur12_24(60);
    setScoreEntrepreneurMoins12(40);
    setMultiplierEntrepreneur36Plus(2.0);
    setMultiplierEntrepreneur24_36(1.8);
    setMultiplierEntrepreneur12_24(1.5);
    setMultiplierEntrepreneurMoins12(1.0);

    // Reset scores secteur informel
    setScoreInformel36Plus(75);
    setScoreInformel24_36(65);
    setScoreInformel12_24(55);
    setScoreInformelMoins12(35);
    setMultiplierInformel36Plus(1.8);
    setMultiplierInformel24_36(1.6);
    setMultiplierInformel12_24(1.3);
    setMultiplierInformelMoins12(1.0);

    // Reset seuils CA
    setSeuilCaEleve(5000000);
    setSeuilCaMoyen(2000000);
    setSeuilCaBas(1000000);
    setBonusCaEleve(10);
    setBonusCaMoyen(5);
    setBonusCaBas(3);

    // Reset score minimum
    setScoreMinimumEligibilite(40);

    // Reset bonifications/malus professionnels
    setBonusProfPatente(5);
    setBonusProfEmployes(3);
    setBonusProfContrats(5);
    setBonusProfEvolutionHausse(5);
    setMalusProfEvolutionBaisse(5);

    // Reset bonifications secteur informel
    setBonusInformelAssociation(5);
    setBonusInformelClients10Plus(5);
    setBonusInformelClients5Plus(3);
    setBonusInformelMarche(3);
    setBonusInformelLocalFixe(3);
    setBonusInformelEpargne(3);
    setBonusInformelMobileMoney(2);

    // Reset bonifications/malus documentaires
    setBonusDocTousVerifies(5);
    setBonusDocTousLisibles(5);
    setBonusDocDossierComplet(3);
    setMalusDocEssentielManquant(5);
    setMalusDocPeuDocuments(10);

    // Reset points personnels - Ancienneté
    setPointsPersonalAnc36Plus(20);
    setPointsPersonalAnc24_36(15);
    setPointsPersonalAnc12_24(10);
    setPointsPersonalAnc6_12(5);

    // Reset points personnels - Famille
    setPointsPersonalFamille3Plus(15);
    setPointsPersonalFamille2Plus(10);
    setPointsPersonalParentIsole(10);

    // Reset points personnels - Logement
    setPointsPersonalLogementRue(25);
    setPointsPersonalLogementInsalubre(20);
    setPointsPersonalLogementHeberge(15);

    // Reset points personnels - Profession prioritaire
    setPointsPersonalProfPrioritaire(10);
  };

  // Sauvegarder (créer ou mettre à jour)
  const handleSave = async () => {
    // Validation des poids
    const totalWeight = scoringWeights.reduce((sum, w) => sum + w.weight, 0);
    if (totalWeight !== 100) {
      notification.toast.warning(`La somme des poids doit être égale à 100% (actuellement ${totalWeight}%)`);
      return;
    }

    // Validation des points financiers
    const totalPointsFinancial = pointsFinancialRevenus + pointsFinancialEndettement + pointsFinancialRav + pointsFinancialEpargne;
    if (totalPointsFinancial !== 100) {
      notification.toast.warning(`La somme des points financiers doit être égale à 100 (actuellement ${totalPointsFinancial})`);
      return;
    }

    if (!configName.trim()) {
      notification.toast.warning('Le nom de la configuration est requis');
      return;
    }

    setSaving(true);

    try {
      const configData = {
        name: configName,
        description: configDescription,
        version: configVersion,
        // Poids (convertir de % en décimal)
        weight_financial: scoringWeights.find(w => w.id === 'financial')!.weight / 100,
        weight_professional: scoringWeights.find(w => w.id === 'professional')!.weight / 100,
        weight_documentary: scoringWeights.find(w => w.id === 'documentary')!.weight / 100,
        weight_eligibility: scoringWeights.find(w => w.id === 'eligibility')!.weight / 100,
        weight_personal: scoringWeights.find(w => w.id === 'personal')!.weight / 100,
        // Seuils financiers
        seuil_revenus_minimal: seuilRevenusMinimal,
        seuil_rav_recommande: seuilRavRecommande,
        plafond_revenus_annuel: plafondRevenusAnnuel,
        // Points financiers
        points_financial_revenus_stables: pointsFinancialRevenus,
        points_financial_taux_endettement: pointsFinancialEndettement,
        points_financial_reste_vivre: pointsFinancialRav,
        points_financial_epargne: pointsFinancialEpargne,
        // Coefficients financiers
        coef_taux_endettement: coefTauxEndettement,
        taux_endettement_defaut: tauxEndettementDefaut,
        mois_loyer_epargne: moisLoyerEpargne,
        seuil_epargne_heuristique: seuilEpargneHeuristique,
        multi_epargne_heuristique: multiEpargneHeuristique,
        // Scores employés
        score_cdi_base: scoreCdiBase,
        score_cdi_multiplier: scoreCdiMultiplier,
        score_cdd_base: scoreCddBase,
        score_cdd_multiplier: scoreCddMultiplier,
        score_fonctionnaire_base: scoreFonctionnaireBase,
        score_fonctionnaire_multiplier: scoreFonctionnaireMultiplier,
        score_stage_base: scoreStageBase,
        score_stage_multiplier: scoreStageMultiplier,
        score_essai_base: scoreEssaiBase,
        score_essai_multiplier: scoreEssaiMultiplier,
        // Scores entrepreneurs
        score_entrepreneur_36plus: scoreEntrepreneur36Plus,
        score_entrepreneur_24_36: scoreEntrepreneur24_36,
        score_entrepreneur_12_24: scoreEntrepreneur12_24,
        score_entrepreneur_moins_12: scoreEntrepreneurMoins12,
        multiplier_entrepreneur_36plus: multiplierEntrepreneur36Plus,
        multiplier_entrepreneur_24_36: multiplierEntrepreneur24_36,
        multiplier_entrepreneur_12_24: multiplierEntrepreneur12_24,
        multiplier_entrepreneur_moins_12: multiplierEntrepreneurMoins12,
        // Scores secteur informel
        score_informel_36plus: scoreInformel36Plus,
        score_informel_24_36: scoreInformel24_36,
        score_informel_12_24: scoreInformel12_24,
        score_informel_moins_12: scoreInformelMoins12,
        multiplier_informel_36plus: multiplierInformel36Plus,
        multiplier_informel_24_36: multiplierInformel24_36,
        multiplier_informel_12_24: multiplierInformel12_24,
        multiplier_informel_moins_12: multiplierInformelMoins12,
        // Seuils CA entrepreneurs
        seuil_ca_eleve: seuilCaEleve,
        seuil_ca_moyen: seuilCaMoyen,
        seuil_ca_bas: seuilCaBas,
        bonus_ca_eleve: bonusCaEleve,
        bonus_ca_moyen: bonusCaMoyen,
        bonus_ca_bas: bonusCaBas,
        // Score minimum d'éligibilité
        score_minimum_eligibilite: scoreMinimumEligibilite,
        // Bonifications/malus professionnels
        bonus_professionnel_patente: bonusProfPatente,
        bonus_professionnel_employes: bonusProfEmployes,
        bonus_professionnel_contrats: bonusProfContrats,
        bonus_professionnel_evolution_hausse: bonusProfEvolutionHausse,
        malus_professionnel_evolution_baisse: malusProfEvolutionBaisse,
        // Bonifications secteur informel
        bonus_informel_association: bonusInformelAssociation,
        bonus_informel_clients_10plus: bonusInformelClients10Plus,
        bonus_informel_clients_5plus: bonusInformelClients5Plus,
        bonus_informel_marche: bonusInformelMarche,
        bonus_informel_local_fixe: bonusInformelLocalFixe,
        bonus_informel_epargne: bonusInformelEpargne,
        bonus_informel_mobile_money: bonusInformelMobileMoney,
        // Bonifications/malus documentaires
        bonus_documentary_tous_verifies: bonusDocTousVerifies,
        bonus_documentary_tous_lisibles: bonusDocTousLisibles,
        bonus_documentary_dossier_complet: bonusDocDossierComplet,
        malus_documentary_document_essentiel: malusDocEssentielManquant,
        malus_documentary_peu_documents: malusDocPeuDocuments,
        // Points personnels - Ancienneté
        points_personal_anciennete_36plus: pointsPersonalAnc36Plus,
        points_personal_anciennete_24_36: pointsPersonalAnc24_36,
        points_personal_anciennete_12_24: pointsPersonalAnc12_24,
        points_personal_anciennete_6_12: pointsPersonalAnc6_12,
        // Points personnels - Famille
        points_personal_famille_3plus: pointsPersonalFamille3Plus,
        points_personal_famille_2plus: pointsPersonalFamille2Plus,
        points_personal_parent_isole: pointsPersonalParentIsole,
        // Points personnels - Logement
        points_personal_logement_rue: pointsPersonalLogementRue,
        points_personal_logement_insalubre: pointsPersonalLogementInsalubre,
        points_personal_logement_heberge: pointsPersonalLogementHeberge,
        // Points personnels - Profession prioritaire
        points_personal_profession_prioritaire: pointsPersonalProfPrioritaire
      };

      let response: any;
      if (isCreatingNew) {
        // Créer une nouvelle configuration
        response = await ScoringConfigurationServices.create(configData);
        if (response.success) {
          notification.toast.success('Configuration créée avec succès !');
          setIsCreatingNew(false);
          setSelectedConfigId(response.data.id);
          await loadConfigurations();
        }
      } else if (selectedConfigId) {
        // Mettre à jour la configuration existante
        response = await ScoringConfigurationServices.update(selectedConfigId, configData);
        if (response.success) {
          notification.toast.success('Configuration mise à jour avec succès !');
          await loadConfigurations();
        }
      }

      if (!response?.success) {
        notification.toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      notification.toast.error(`Erreur: ${error.message || 'Impossible de sauvegarder la configuration'}`);
    } finally {
      setSaving(false);
    }
  };

  // Activer une configuration
  const handleActivate = async (configId: number) => {
    if (window.confirm('Activer cette configuration ? Cela désactivera automatiquement les autres configurations.')) {
      try {
        const response = await ScoringConfigurationServices.activate(configId) as any;
        if (response.success) {
          notification.toast.success('Configuration activée avec succès !');
          await loadConfigurations();
        } else {
          notification.toast.error('Erreur lors de l\'activation');
        }
      } catch (error: any) {
        console.error('Error activating configuration:', error);
        notification.toast.error(`Erreur: ${error.message || 'Impossible d\'activer la configuration'}`);
      }
    }
  };

  // Supprimer une configuration
  const handleDelete = async (configId: number) => {
    const config = allConfigs.find(c => c.id === configId);
    if (config?.is_active) {
      notification.toast.warning('Impossible de supprimer la configuration active');
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette configuration ? Cette action est irréversible.')) {
      try {
        const response = await ScoringConfigurationServices.delete(configId) as any;
        if (response.success) {
          notification.toast.success('Configuration supprimée avec succès !');
          if (selectedConfigId === configId) {
            setSelectedConfigId(null);
            setIsCreatingNew(false);
          }
          await loadConfigurations();
        } else {
          notification.toast.error('Erreur lors de la suppression');
        }
      } catch (error: any) {
        console.error('Error deleting configuration:', error);
        notification.toast.error(`Erreur: ${error.message || 'Impossible de supprimer la configuration'}`);
      }
    }
  };

  const handleReset = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser aux valeurs par défaut ?')) {
      setScoringWeights([
        { id: 'financial', label: 'Critère Financier', weight: 35, description: 'Revenus, taux d\'endettement, reste à vivre, épargne' },
        { id: 'professional', label: 'Critère Professionnel', weight: 25, description: 'Type de contrat, ancienneté, stabilité professionnelle' },
        { id: 'documentary', label: 'Critère Documentaire', weight: 20, description: 'Complétude et qualité des documents fournis' },
        { id: 'eligibility', label: 'Critère d\'Éligibilité', weight: 15, description: 'Conformité aux critères d\'éligibilité de base' },
        { id: 'personal', label: 'Critère Personnel', weight: 5, description: 'Ancienneté commune, situation familiale, handicap' }
      ]);
      notification.toast.info('Valeurs réinitialisées (non sauvegardées)');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Container>
          <div style={{
            textAlign: 'center',
            padding: theme.spacing['3xl'],
            color: theme.colors.textSecondary
          }}>
            Chargement de la configuration...
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  const selectedConfig = allConfigs.find(c => c.id === selectedConfigId);

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <TitleSection>
            <Title>Configuration Système</Title>
            <Subtitle>Gérer les configurations de scoring et d'évaluation</Subtitle>
          </TitleSection>
          <ActionButtons>
            <Button variant="secondary" onClick={handleCreateNew}>
              <FiSettings /> Nouvelle Configuration
            </Button>
          </ActionButtons>
        </Header>

        {/* Configuration Selector */}
        <ConfigSelector>
          <SectionTitle>
            <FiCpu /> Configurations Disponibles
          </SectionTitle>
          <SectionDescription>
            Sélectionnez une configuration à modifier ou créez-en une nouvelle
          </SectionDescription>
          <ConfigList>
            {allConfigs.map(config => (
              <ConfigCard
                key={config.id}
                active={config.is_active}
                selected={config.id === selectedConfigId}
                onClick={() => handleSelectConfig(config.id)}
              >
                <ConfigCardHeader>
                  <div>
                    <ConfigName>{config.name}</ConfigName>
                    <ConfigBadge type={config.is_active ? 'active' : 'inactive'}>
                      {config.is_active ? '✓ Active' : 'Inactive'}
                    </ConfigBadge>
                  </div>
                </ConfigCardHeader>
                <ConfigMeta>
                  Version: {config.version}<br />
                  {config.description && <>{config.description}<br /></>}
                  Modifiée: {new Date(config.updated_at).toLocaleDateString()}
                </ConfigMeta>
                <ConfigActions onClick={(e) => e.stopPropagation()}>
                  {!config.is_active && (
                    <SmallButton variant="success" onClick={() => handleActivate(config.id)}>
                      Activer
                    </SmallButton>
                  )}
                  {!config.is_active && (
                    <SmallButton variant="danger" onClick={() => handleDelete(config.id)}>
                      Supprimer
                    </SmallButton>
                  )}
                </ConfigActions>
              </ConfigCard>
            ))}
          </ConfigList>
        </ConfigSelector>

        {/* Form Editor - Only show if a config is selected or creating new */}
        {(selectedConfigId || isCreatingNew) && (
          <>
            <Section>
              <SectionTitle>
                <FiSettings /> {isCreatingNew ? 'Nouvelle Configuration' : 'Modifier la Configuration'}
              </SectionTitle>
              <SectionDescription>
                {isCreatingNew
                  ? 'Créez une nouvelle configuration de scoring'
                  : `Modification de: ${selectedConfig?.name}`}
              </SectionDescription>

              {/* Metadata Fields */}
              <FormGroup style={{ marginBottom: theme.spacing.xl }}>
                <Label>Nom de la Configuration *</Label>
                <Input
                  type="text"
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                  placeholder="Ex: Configuration Par Défaut"
                />
              </FormGroup>

              <FormGroup style={{ marginBottom: theme.spacing.xl }}>
                <Label>Description</Label>
                <TextArea
                  value={configDescription}
                  onChange={(e) => setConfigDescription(e.target.value)}
                  placeholder="Description de cette configuration..."
                  rows={3}
                />
              </FormGroup>

              <FormGroup style={{ marginBottom: theme.spacing.xl }}>
                <Label>Version</Label>
                <Input
                  type="text"
                  value={configVersion}
                  onChange={(e) => setConfigVersion(e.target.value)}
                  placeholder="Ex: 1.0.0"
                />
              </FormGroup>

              <div style={{
                display: 'flex',
                gap: theme.spacing.md,
                marginBottom: theme.spacing.xl
              }}>
                <Button onClick={handleSave} disabled={saving}>
                  <FiSave /> {saving ? 'Sauvegarde...' : (isCreatingNew ? 'Créer' : 'Mettre à jour')}
                </Button>
                <Button variant="secondary" onClick={handleReset}>
                  <FiRotateCcw /> Réinitialiser les poids
                </Button>
              </div>
            </Section>

            <TabBar>
          <Tab active={activeTab === 'scoring'} onClick={() => setActiveTab('scoring')}>
            <FiSliders /> Paramètres Scoring
          </Tab>
          <Tab active={activeTab === 'workflow'} onClick={() => setActiveTab('workflow')}>
            <FiZap /> Workflow
          </Tab>
          <Tab active={activeTab === 'customization'} onClick={() => setActiveTab('customization')}>
            <FiSettings /> Personnalisation
          </Tab>
        </TabBar>

        {activeTab === 'scoring' && (
          <>
            <Section>
              <SectionTitle>
                <FiSliders /> Poids des Critères
              </SectionTitle>
              <SectionDescription>
                Ajustez l'importance de chaque critère dans le calcul du score final.
                La somme doit être égale à 100%.
              </SectionDescription>
              <WeightsList>
                {scoringWeights.map(weight => (
                  <WeightItem key={weight.id}>
                    <WeightHeader>
                      <WeightInfo>
                        <WeightLabel>{weight.label}</WeightLabel>
                        <WeightDescription>{weight.description}</WeightDescription>
                      </WeightInfo>
                      <WeightControl>
                        <Slider
                          type="range"
                          min="0"
                          max="100"
                          value={weight.weight}
                          onChange={(e) => handleWeightChange(weight.id, parseInt(e.target.value))}
                          style={{ '--value': `${weight.weight}%` } as React.CSSProperties}
                        />
                        <WeightValue>{weight.weight}%</WeightValue>
                      </WeightControl>
                    </WeightHeader>
                  </WeightItem>
                ))}
                <div style={{
                  padding: theme.spacing.md,
                  background: scoringWeights.reduce((sum, w) => sum + w.weight, 0) === 100
                    ? theme.colors.success + '20'
                    : theme.colors.warning + '20',
                  borderRadius: theme.borderRadius.md,
                  textAlign: 'center',
                  fontWeight: theme.typography.weights.bold
                }}>
                  Total: {scoringWeights.reduce((sum, w) => sum + w.weight, 0)}%
                  {scoringWeights.reduce((sum, w) => sum + w.weight, 0) !== 100 && ' ⚠️ Doit être égal à 100%'}
                </div>
              </WeightsList>
            </Section>

            <Section>
              <SectionTitle>
                <FiTrendingUp /> Seuils Financiers
              </SectionTitle>
              <SectionDescription>
                Définissez les seuils de revenus et restes à vivre pour l'évaluation financière.
              </SectionDescription>
              <ThresholdGrid>
                <FormGroup>
                  <Label>Revenu Mensuel Minimal (FCFA)</Label>
                  <Input
                    type="number"
                    value={seuilRevenusMinimal}
                    onChange={(e) => setSeuilRevenusMinimal(Number(e.target.value))}
                    placeholder="2 500 000"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Reste à Vivre Recommandé (FCFA)</Label>
                  <Input
                    type="number"
                    value={seuilRavRecommande}
                    onChange={(e) => setSeuilRavRecommande(Number(e.target.value))}
                    placeholder="500 000"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Plafond Revenus Annuel (FCFA)</Label>
                  <Input
                    type="number"
                    value={plafondRevenusAnnuel}
                    onChange={(e) => setPlafondRevenusAnnuel(Number(e.target.value))}
                    placeholder="35 362 140"
                  />
                </FormGroup>
              </ThresholdGrid>
            </Section>

            <Section>
              <SectionTitle>
                <FiSliders /> Répartition Points - Critère Financier
              </SectionTitle>
              <SectionDescription>
                Définissez la répartition des 100 points du critère financier entre les sous-critères.
              </SectionDescription>
              <ThresholdGrid>
                <FormGroup>
                  <Label>Points - Revenus Stables (sur 100)</Label>
                  <Input
                    type="number"
                    value={pointsFinancialRevenus}
                    onChange={(e) => setPointsFinancialRevenus(Number(e.target.value))}
                    min="0"
                    max="100"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - Taux d'Endettement (sur 100)</Label>
                  <Input
                    type="number"
                    value={pointsFinancialEndettement}
                    onChange={(e) => setPointsFinancialEndettement(Number(e.target.value))}
                    min="0"
                    max="100"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - Reste à Vivre (sur 100)</Label>
                  <Input
                    type="number"
                    value={pointsFinancialRav}
                    onChange={(e) => setPointsFinancialRav(Number(e.target.value))}
                    min="0"
                    max="100"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - Épargne Disponible (sur 100)</Label>
                  <Input
                    type="number"
                    value={pointsFinancialEpargne}
                    onChange={(e) => setPointsFinancialEpargne(Number(e.target.value))}
                    min="0"
                    max="100"
                  />
                </FormGroup>
              </ThresholdGrid>
              <div style={{
                padding: theme.spacing.md,
                background: (pointsFinancialRevenus + pointsFinancialEndettement + pointsFinancialRav + pointsFinancialEpargne) === 100
                  ? theme.colors.success + '20'
                  : theme.colors.warning + '20',
                borderRadius: theme.borderRadius.md,
                textAlign: 'center',
                fontWeight: theme.typography.weights.bold,
                marginTop: theme.spacing.md
              }}>
                Total: {pointsFinancialRevenus + pointsFinancialEndettement + pointsFinancialRav + pointsFinancialEpargne}/100
                {(pointsFinancialRevenus + pointsFinancialEndettement + pointsFinancialRav + pointsFinancialEpargne) !== 100 && ' ⚠️ Doit être égal à 100'}
              </div>
            </Section>

            <Section>
              <SectionTitle>
                <FiCheck /> Score Minimum d'Éligibilité
              </SectionTitle>
              <SectionDescription>
                Score minimum requis pour qu'une candidature soit considérée comme éligible.
              </SectionDescription>
              <FormGroup style={{ maxWidth: '300px' }}>
                <Label>Score Minimum (sur 100)</Label>
                <Input
                  type="number"
                  value={scoreMinimumEligibilite}
                  onChange={(e) => setScoreMinimumEligibilite(Number(e.target.value))}
                  min="0"
                  max="100"
                  placeholder="40"
                />
                <div style={{
                  fontSize: theme.typography.sizes.xs,
                  color: theme.colors.text + '60',
                  marginTop: theme.spacing.xs
                }}>
                  Les candidatures avec un score inférieur à {scoreMinimumEligibilite} seront automatiquement rejetées.
                </div>
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>
                <FiTrendingUp /> Bonifications Professionnelles
              </SectionTitle>
              <SectionDescription>
                Points bonus pour les profils professionnels (employés/entrepreneurs).
              </SectionDescription>
              <ThresholdGrid>
                <FormGroup>
                  <Label>Bonus - Patente/RCCM/IFU</Label>
                  <Input
                    type="number"
                    value={bonusProfPatente}
                    onChange={(e) => setBonusProfPatente(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Employés (5+)</Label>
                  <Input
                    type="number"
                    value={bonusProfEmployes}
                    onChange={(e) => setBonusProfEmployes(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Contrats réguliers</Label>
                  <Input
                    type="number"
                    value={bonusProfContrats}
                    onChange={(e) => setBonusProfContrats(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Évolution en hausse</Label>
                  <Input
                    type="number"
                    value={bonusProfEvolutionHausse}
                    onChange={(e) => setBonusProfEvolutionHausse(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Malus - Évolution en baisse</Label>
                  <Input
                    type="number"
                    value={malusProfEvolutionBaisse}
                    onChange={(e) => setMalusProfEvolutionBaisse(Number(e.target.value))}
                  />
                </FormGroup>
              </ThresholdGrid>
            </Section>

            <Section>
              <SectionTitle>
                <FiHome /> Bonifications Secteur Informel
              </SectionTitle>
              <SectionDescription>
                Points bonus pour les profils du secteur informel.
              </SectionDescription>
              <ThresholdGrid>
                <FormGroup>
                  <Label>Bonus - Association professionnelle</Label>
                  <Input
                    type="number"
                    value={bonusInformelAssociation}
                    onChange={(e) => setBonusInformelAssociation(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Clients réguliers (10+)</Label>
                  <Input
                    type="number"
                    value={bonusInformelClients10Plus}
                    onChange={(e) => setBonusInformelClients10Plus(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Clients réguliers (5+)</Label>
                  <Input
                    type="number"
                    value={bonusInformelClients5Plus}
                    onChange={(e) => setBonusInformelClients5Plus(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Travaille au marché</Label>
                  <Input
                    type="number"
                    value={bonusInformelMarche}
                    onChange={(e) => setBonusInformelMarche(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Local fixe</Label>
                  <Input
                    type="number"
                    value={bonusInformelLocalFixe}
                    onChange={(e) => setBonusInformelLocalFixe(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Épargne régulière</Label>
                  <Input
                    type="number"
                    value={bonusInformelEpargne}
                    onChange={(e) => setBonusInformelEpargne(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Mobile Money</Label>
                  <Input
                    type="number"
                    value={bonusInformelMobileMoney}
                    onChange={(e) => setBonusInformelMobileMoney(Number(e.target.value))}
                  />
                </FormGroup>
              </ThresholdGrid>
            </Section>

            <Section>
              <SectionTitle>
                <FiSettings /> Configuration Documentaire
              </SectionTitle>
              <SectionDescription>
                Bonifications et pénalités liées à la complétude du dossier.
              </SectionDescription>
              <ThresholdGrid>
                <FormGroup>
                  <Label>Bonus - Tous documents vérifiés</Label>
                  <Input
                    type="number"
                    value={bonusDocTousVerifies}
                    onChange={(e) => setBonusDocTousVerifies(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Tous documents lisibles</Label>
                  <Input
                    type="number"
                    value={bonusDocTousLisibles}
                    onChange={(e) => setBonusDocTousLisibles(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Bonus - Dossier complet (8+ docs)</Label>
                  <Input
                    type="number"
                    value={bonusDocDossierComplet}
                    onChange={(e) => setBonusDocDossierComplet(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Malus - Document essentiel manquant</Label>
                  <Input
                    type="number"
                    value={malusDocEssentielManquant}
                    onChange={(e) => setMalusDocEssentielManquant(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Malus - Peu de documents (&lt;3)</Label>
                  <Input
                    type="number"
                    value={malusDocPeuDocuments}
                    onChange={(e) => setMalusDocPeuDocuments(Number(e.target.value))}
                  />
                </FormGroup>
              </ThresholdGrid>
            </Section>

            <Section>
              <SectionTitle>
                <FiSliders /> Points Personnels - Ancienneté Demande
              </SectionTitle>
              <SectionDescription>
                Points attribués selon l'ancienneté de la demande de logement.
              </SectionDescription>
              <ThresholdGrid>
                <FormGroup>
                  <Label>Points - 36+ mois</Label>
                  <Input
                    type="number"
                    value={pointsPersonalAnc36Plus}
                    onChange={(e) => setPointsPersonalAnc36Plus(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - 24-36 mois</Label>
                  <Input
                    type="number"
                    value={pointsPersonalAnc24_36}
                    onChange={(e) => setPointsPersonalAnc24_36(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - 12-24 mois</Label>
                  <Input
                    type="number"
                    value={pointsPersonalAnc12_24}
                    onChange={(e) => setPointsPersonalAnc12_24(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - 6-12 mois</Label>
                  <Input
                    type="number"
                    value={pointsPersonalAnc6_12}
                    onChange={(e) => setPointsPersonalAnc6_12(Number(e.target.value))}
                  />
                </FormGroup>
              </ThresholdGrid>
            </Section>

            <Section>
              <SectionTitle>
                <FiHome /> Points Personnels - Situation Familiale
              </SectionTitle>
              <SectionDescription>
                Points selon la composition et la situation familiale.
              </SectionDescription>
              <ThresholdGrid>
                <FormGroup>
                  <Label>Points - Famille nombreuse (3+ enfants)</Label>
                  <Input
                    type="number"
                    value={pointsPersonalFamille3Plus}
                    onChange={(e) => setPointsPersonalFamille3Plus(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - Famille (2+ enfants)</Label>
                  <Input
                    type="number"
                    value={pointsPersonalFamille2Plus}
                    onChange={(e) => setPointsPersonalFamille2Plus(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - Parent isolé</Label>
                  <Input
                    type="number"
                    value={pointsPersonalParentIsole}
                    onChange={(e) => setPointsPersonalParentIsole(Number(e.target.value))}
                  />
                </FormGroup>
              </ThresholdGrid>
            </Section>

            <Section>
              <SectionTitle>
                <FiHome /> Points Personnels - Situation Logement
              </SectionTitle>
              <SectionDescription>
                Points selon la précarité de la situation de logement actuelle.
              </SectionDescription>
              <ThresholdGrid>
                <FormGroup>
                  <Label>Points - Sans domicile (rue)</Label>
                  <Input
                    type="number"
                    value={pointsPersonalLogementRue}
                    onChange={(e) => setPointsPersonalLogementRue(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - Logement insalubre/précaire</Label>
                  <Input
                    type="number"
                    value={pointsPersonalLogementInsalubre}
                    onChange={(e) => setPointsPersonalLogementInsalubre(Number(e.target.value))}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Points - Hébergé chez famille</Label>
                  <Input
                    type="number"
                    value={pointsPersonalLogementHeberge}
                    onChange={(e) => setPointsPersonalLogementHeberge(Number(e.target.value))}
                  />
                </FormGroup>
              </ThresholdGrid>
            </Section>

            <Section>
              <SectionTitle>
                <FiCheck /> Points Personnels - Profession Prioritaire
              </SectionTitle>
              <SectionDescription>
                Points bonus pour les professions prioritaires (santé, éducation, sécurité).
              </SectionDescription>
              <FormGroup style={{ maxWidth: '300px' }}>
                <Label>Points - Profession prioritaire</Label>
                <Input
                  type="number"
                  value={pointsPersonalProfPrioritaire}
                  onChange={(e) => setPointsPersonalProfPrioritaire(Number(e.target.value))}
                />
              </FormGroup>
            </Section>
          </>
        )}

        {activeTab === 'workflow' && (
          <Section>
            <SectionTitle>
              <FiZap /> Étapes du Workflow
            </SectionTitle>
            <SectionDescription>
              Configurez les délais et notifications pour chaque étape du processus.
            </SectionDescription>
            <WorkflowList>
              {workflowSteps.map(step => (
                <WorkflowStep key={step.id}>
                  <StepInfo>
                    <StepName>{step.name}</StepName>
                    <StepMeta>
                      Délai: {step.duration}h |
                      Escalade: {step.autoEscalate ? '✓' : '✗'} |
                      Notifications: {step.notifyOnDelay ? '✓' : '✗'}
                    </StepMeta>
                  </StepInfo>
                  <StepControls>
                    <FormGroup>
                      <Label>Délai (heures)</Label>
                      <Input
                        type="number"
                        value={step.duration}
                        min="1"
                        style={{ width: '80px' }}
                        onChange={(e) => setWorkflowSteps(prev =>
                          prev.map(s => s.id === step.id
                            ? { ...s, duration: parseInt(e.target.value) }
                            : s
                          )
                        )}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Auto-escalade</Label>
                      <Toggle>
                        <input
                          type="checkbox"
                          checked={step.autoEscalate}
                          onChange={(e) => setWorkflowSteps(prev =>
                            prev.map(s => s.id === step.id
                              ? { ...s, autoEscalate: e.target.checked }
                              : s
                            )
                          )}
                        />
                        <ToggleSlider />
                      </Toggle>
                    </FormGroup>
                    <FormGroup>
                      <Label>Notifier retard</Label>
                      <Toggle>
                        <input
                          type="checkbox"
                          checked={step.notifyOnDelay}
                          onChange={(e) => setWorkflowSteps(prev =>
                            prev.map(s => s.id === step.id
                              ? { ...s, notifyOnDelay: e.target.checked }
                              : s
                            )
                          )}
                        />
                        <ToggleSlider />
                      </Toggle>
                    </FormGroup>
                  </StepControls>
                </WorkflowStep>
              ))}
            </WorkflowList>
          </Section>
        )}

        {activeTab === 'customization' && (
          <Section>
            <SectionTitle>
              <FiSettings /> Personnalisation de l'Interface
            </SectionTitle>
            <SectionDescription>
              Personnalisez l'apparence et les messages de votre plateforme.
            </SectionDescription>
            <CustomizationGrid>
              <div>
                <FormGroup>
                  <Label>Couleur Principale</Label>
                  <ColorPicker type="color" defaultValue={theme.colors.primary} />
                </FormGroup>
                <FormGroup>
                  <Label>Couleur Secondaire</Label>
                  <ColorPicker type="color" defaultValue={theme.colors.info} />
                </FormGroup>
                <FormGroup>
                  <Label>Langue de l'Interface</Label>
                  <Select>
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                  </Select>
                </FormGroup>
              </div>
              <div>
                <FormGroup>
                  <Label>Message d'Accueil Candidats</Label>
                  <TextArea
                    placeholder="Bienvenue sur notre plateforme d'attribution de logements..."
                    defaultValue="Bienvenue sur notre plateforme d'attribution de logements sociaux."
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Signature Email Automatique</Label>
                  <TextArea
                    placeholder="Cordialement, L'équipe..."
                    defaultValue="Cordialement,&#10;L'équipe d'attribution"
                  />
                </FormGroup>
              </div>
            </CustomizationGrid>
          </Section>
        )}
          </>
        )}
      </Container>
    </DashboardLayout>
  );
};
